/**
 * LinkDialog - Modal for adding/editing hyperlinks
 *
 * Features:
 * - URL input with validation
 * - Text display (selected text becomes link text)
 * - Open in new tab option
 * - Remove link button for existing links
 * - Keyboard shortcuts (Enter to save, Escape to cancel)
 *
 * How it works:
 * 1. User selects text in editor and clicks Link button in BubbleToolbar
 * 2. This dialog opens with the selected text context
 * 3. User enters a URL (https:// is auto-added if missing)
 * 4. On save, the Tiptap editor's setLink command applies the hyperlink
 * 5. If editing an existing link, "Remove Link" button removes it
 *
 * Usage:
 * ```tsx
 * <LinkDialog
 *   editor={editor}
 *   isOpen={isDialogOpen}
 *   onClose={() => setIsDialogOpen(false)}
 *   initialUrl={currentLinkUrl}
 * />
 * ```
 */

import { useState, useEffect, useRef } from 'react';
import type { Editor } from '@tiptap/react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for the LinkDialog component
 */
interface LinkDialogProps {
  /** Tiptap editor instance - used to apply link changes */
  editor: Editor;
  /** Whether dialog is open (controlled externally) */
  isOpen: boolean;
  /** Close callback - called when user cancels or saves */
  onClose: () => void;
  /** Initial URL (for editing existing link), empty string for new links */
  initialUrl?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function LinkDialog({
  editor,
  isOpen,
  onClose,
  initialUrl = '',
}: LinkDialogProps) {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  // URL input value
  const [url, setUrl] = useState(initialUrl);

  // Validation error message (null if no error)
  const [error, setError] = useState<string | null>(null);

  // Ref to input for auto-focus
  const inputRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // Reset state and focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Set URL to initial value (empty for new, current href for edit)
      setUrl(initialUrl);
      // Clear any previous error
      setError(null);
      // Focus input after a small delay (ensures dialog is rendered)
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, initialUrl]);

  // Handle keyboard shortcuts (Enter = save, Escape = close)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if dialog is open
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, url]); // Re-add listener when url changes (for handleSave)

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Validate URL format
   *
   * Rules:
   * - URL cannot be empty
   * - Must be a valid URL (we auto-add https:// if missing)
   *
   * @param value - URL string to validate
   * @returns true if valid, false and sets error if invalid
   */
  const validateUrl = (value: string): boolean => {
    // Check for empty URL
    if (!value.trim()) {
      setError('URL is required');
      return false;
    }

    // Try to construct URL to validate format
    // Add https:// if no protocol specified (user-friendly)
    try {
      const urlToCheck = value.includes('://') ? value : `https://${value}`;
      new URL(urlToCheck);
      return true;
    } catch {
      setError('Please enter a valid URL');
      return false;
    }
  };

  /**
   * Save the link to the editor
   *
   * Steps:
   * 1. Validate URL
   * 2. Add https:// if missing
   * 3. Apply link to selected text using Tiptap chain
   * 4. Close dialog
   */
  const handleSave = () => {
    // Validate first
    if (!validateUrl(url)) return;

    // Add protocol if missing (user-friendly - can type "google.com")
    const finalUrl = url.includes('://') ? url : `https://${url}`;

    // Apply link to selected text
    // - focus(): bring focus back to editor
    // - extendMarkRange('link'): expand selection to cover full link if partial
    // - setLink(): apply the link mark with href attribute
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: finalUrl })
      .run();

    // Close dialog
    onClose();
  };

  /**
   * Remove the link from selected text
   *
   * Used when editing an existing link and user wants to un-link it.
   */
  const handleRemove = () => {
    // Remove link mark from selection
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  // Don't render anything if dialog is closed
  if (!isOpen) return null;

  // Determine if we're editing an existing link (for UI differences)
  const hasExistingLink = !!initialUrl;

  return (
    // Overlay - clicking outside closes dialog
    <div className="link-dialog-overlay" onClick={onClose}>
      {/* Dialog box - stop propagation so clicking inside doesn't close */}
      <div
        className="link-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-dialog-title"
      >
        {/* Title - changes based on edit vs new */}
        <h3 id="link-dialog-title" className="link-dialog-title">
          {hasExistingLink ? 'Edit Link' : 'Add Link'}
        </h3>

        {/* URL input field */}
        <div className="link-dialog-field">
          <label htmlFor="link-url" className="link-dialog-label">
            URL
          </label>
          <input
            ref={inputRef}
            id="link-url"
            type="text"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              // Clear error on change (user is fixing it)
              setError(null);
            }}
            placeholder="https://example.com"
            className={`link-dialog-input ${error ? 'has-error' : ''}`}
          />
          {/* Error message below input */}
          {error && <p className="link-dialog-error">{error}</p>}
        </div>

        {/* Action buttons */}
        <div className="link-dialog-actions">
          {/* Remove Link - only shown when editing existing link */}
          {hasExistingLink && (
            <button
              type="button"
              onClick={handleRemove}
              className="link-dialog-button danger"
            >
              Remove Link
            </button>
          )}

          {/* Right-aligned: Cancel and Save/Update */}
          <div className="link-dialog-actions-right">
            <button
              type="button"
              onClick={onClose}
              className="link-dialog-button secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="link-dialog-button primary"
            >
              {hasExistingLink ? 'Update' : 'Add Link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
